import { ETextStyleVariant, Selector } from '@outdoorsyco/bonfire';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { getDeliveryRadius } from '@/redux/selectors/listing/delivery';
import { EDeliveryOption, EDeliveryTypes } from '@/services/types/core/delivery.types';

// Map EDeliveryOption to EDeliveryTypes
const mapDeliveryOptionToType = (option: EDeliveryOption): EDeliveryTypes | null => {
  if (option === EDeliveryOption.PICKUP) return EDeliveryTypes.PICKUP;
  if (option === EDeliveryOption.STATIONARY) return EDeliveryTypes.DELIVERY;
  if (option === EDeliveryOption.MOVING) return EDeliveryTypes.DELIVERY;
  if (option === EDeliveryOption.RV_STAY) return EDeliveryTypes.DELIVERY;
  return null;
};

type IDeliveryOptionsElement = React.HTMLAttributes<HTMLElement>;

const options: { id: string; value: EDeliveryTypes }[] = [
  { id: 'pickup', value: EDeliveryTypes.PICKUP },
  { id: 'delivery', value: EDeliveryTypes.DELIVERY },
];

interface IProps extends IDeliveryOptionsElement {
  deliveryType: EDeliveryOption;
  deliveryCostPerMile: string | undefined;
  onChangeOption: (value: EDeliveryTypes) => void;
  onSelectPickup?: (isPickup: boolean) => void;
  setIsOpen?: (isOpen: boolean) => void;
}

const DeliveryModalOptions: React.FC<IProps> = ({
  deliveryType,
  onChangeOption,
  deliveryCostPerMile,
}) => {
  const deliveryRadius = useSelector(getDeliveryRadius);
  const intl = useIntl();

  const deliveryOptionsMap = {
    [EDeliveryTypes.PICKUP]: {
      title: intl.formatMessage({
        defaultMessage: 'Pick up - Free',
        id: 'uC0jCq',
      }),
      description: intl.formatMessage({
        defaultMessage: "I'll pick up my RV at the host's set location.",
        id: 'H2t6uK',
      }),
    },
    [EDeliveryTypes.DELIVERY]: {
      title: intl.formatMessage(
        { defaultMessage: 'Delivery - {deliveryCostPerMile} per {unit}', id: 'K7C/Sx' },
        { deliveryCostPerMile, unit: deliveryRadius?.unit || 'mile' },
      ),
      description: intl.formatMessage(
        {
          defaultMessage: 'This host delivers to locations within a {radius} {unit} radius.',
          id: 'oAMGhA',
        },
        { radius: deliveryRadius?.radius, unit: deliveryRadius?.unit || 'mile' },
      ),
    },
  };

  return (
    <div className="pb-4 mt-4">
      <fieldset className="grid gap-x-default gap-y-4 md:grid-flow-col">
        {options.map(option => {
          const { id, value } = option;
          const isChecked = mapDeliveryOptionToType(deliveryType) === value;
          const { title, description } = deliveryOptionsMap[value];

          return (
            <div className="block" key={id}>
              <Selector
                label={title}
                ariaLabel={title}
                labelVariant={ETextStyleVariant.MediumBold}
                name="delivery"
                description={description}
                descriptionClassName="text-gray-500"
                labelClassName="h-full"
                value={value}
                onChange={() => onChangeOption(value)}
                showIndicator
                isSelected={isChecked}
                data-testid={`${id}-btn`}
              />
            </div>
          );
        })}
      </fieldset>
    </div>
  );
};

export default DeliveryModalOptions;
